import {
  isImage,
  isFilm,
  isImageAndFilm,
  isCreativeOnly,
  isEditorialOnly,
  isCreativeAndEditorial,
  type SearchParameters,
} from '../searchParameters';
import { PreferenceScopeEnum } from './preferenceScope';

const imageScope = (
  searchParameters: SearchParameters
): PreferenceScopeEnum => {
  if (isEditorialOnly(searchParameters))
    return PreferenceScopeEnum.EDITORIAL_IMAGE;
  if (isCreativeOnly(searchParameters))
    return PreferenceScopeEnum.CREATIVE_IMAGE;
  return PreferenceScopeEnum.MIXED_IMAGE;
};

const filmScope = (searchParameters: SearchParameters): PreferenceScopeEnum => {
  if (isCreativeAndEditorial(searchParameters))
    return PreferenceScopeEnum.MIXED_FILM;
  if (isEditorialOnly(searchParameters))
    return PreferenceScopeEnum.EDITORIAL_FILM;
  return PreferenceScopeEnum.CREATIVE_FILM;
};

const imageAndFilmScope = (
  searchParameters: SearchParameters
): PreferenceScopeEnum | undefined =>
  isCreativeOnly(searchParameters)
    ? PreferenceScopeEnum.CREATIVE_IMAGE_FILM
    : undefined;

/**
 * The remembered scope a search falls in, or `undefined` when it falls in none.
 *
 * Named for preferences because that is where the scope first mattered, but it
 * is the scope itself: which licenses a search may carry keys off the same
 * answer.
 *
 * - **Video-template searches return `undefined`.** A video-template search is
 *   not a storage scope and nothing is ever read or written there. Licenses
 *   treat it the same way, offering none.
 * - **`ANALOG_FILM` is never returned.** It is a real storage slot that no
 *   search resolves to.
 *
 * Callers must supply a `SearchParameters` whose `assettype` reflects the
 * route. The schema default is `image,film`, while `/search/2/image` implies
 * `image`, so a bare query string resolves through the image-and-film branch
 * rather than an image scope.
 */
export const scopeRememberedFor = (
  searchParameters: SearchParameters
): PreferenceScopeEnum | undefined => {
  if (isImage(searchParameters)) return imageScope(searchParameters);
  if (isFilm(searchParameters)) return filmScope(searchParameters);
  if (isImageAndFilm(searchParameters))
    return imageAndFilmScope(searchParameters);

  return undefined;
};
